import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon, IconName } from '@hills-design-system/components';

export interface DropdownInputOption {
  id: string;
  label: string;
  disabled?: boolean;
  description?: string;
  searchText?: string; // When provided, used for search filtering instead of label
}

export interface DropdownInputProps {
  id: string;
  label: string;
  placeholder?: string;
  options: DropdownInputOption[];
  value?: string;
  onChange: (id: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  canSearch?: boolean;
  /**
   * While true, an empty option list reads as loading rather than as no matches.
   */
  isLoading?: boolean;
  /**
   * Optional custom render function for each option
   */
  renderOption?: (option: DropdownInputOption, selected: boolean) => React.ReactNode;
}

const DropdownInput: React.FC<DropdownInputProps> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  helperText,
  className = '',
  canSearch = false,
  isLoading = false,
  renderOption,
}) => {
  const hasValue = value !== undefined && value !== '';
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!canSearch || !searchTerm) {
      return options;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter((opt) => {
      const searchableLabel = (opt.searchText ?? opt.label).toLowerCase();
      return (
        searchableLabel.includes(lowerSearchTerm) ||
        opt.description?.toLowerCase().includes(lowerSearchTerm) ||
        opt.id?.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [options, searchTerm, canSearch]);

  // Reset search when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    } else if (canSearch) {
      // This prevents the browser from jumping the page scroll to the bottom of the document
      searchInputRef.current?.focus({
        preventScroll: true,
      });
    }
  }, [isOpen, canSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate menu position when opened
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && fieldRef.current) {
        const rect = fieldRef.current.getBoundingClientRect();
        setMenuStyle({
          position: 'fixed', // Changed from absolute
          top: `${rect.bottom + 4}px`, // No scrollY needed for 'fixed'
          left: `${rect.left}px`, // No scrollX needed
          width: `${rect.width}px`,
          zIndex: 9999,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      // Add scroll listener to window to update position as you scroll
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => {
            const nextIndex = prev < options.length - 1 ? prev + 1 : 0;
            return options[nextIndex].disabled ? prev : nextIndex;
          });
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : options.length - 1;
            return options[nextIndex].disabled ? prev : nextIndex;
          });
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          handleOptionClick(event, options[highlightedIndex].id);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleOptionClick = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    optionId: string
  ) => {
    e.stopPropagation(); // Prevents the modal from closing
    onChange(optionId);
    setIsOpen(false);
  };

  const renderMenu = () => (
    <div
      ref={menuRef}
      style={menuStyle}
      className="z-50 w-full rounded-200 bg-white shadow-300 border border-gray-100 max-h-[300px] h-fit flex flex-col overflow-hidden"
      data-dropdown-menu="true"
      role="menu"
      id={`${id}-listbox`}
    >
      {/* Search Input Field */}
      {canSearch && (
        <div className="p-2 border-b border-gray-100 bg-gray-50 sticky top-0">
          <input
            ref={searchInputRef}
            type="text"
            className="w-full p-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Search..."
            autoComplete="off"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            onInput={(e) => {
              const domVal = (e.target as HTMLInputElement).value;
              if (domVal !== searchTerm) {
                setSearchTerm(domVal);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="overflow-auto h-fit">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => (
            <button
              key={`${option.id}-${index}`}
              type="button"
              className={`w-full text-left block px-4 py-3 text-sm cursor-pointer
              ${option.disabled ? 'text-black-200 cursor-not-allowed' : 'text-gray-1100 hover:bg-blue-50'}
              ${option.id === value ? 'bg-blue-100 font-medium' : ''}
              ${index === highlightedIndex ? 'bg-blue-50' : ''}`}
              onClick={(e) => !option.disabled && handleOptionClick(e, option.id)}
              onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
              disabled={option.disabled}
              role="menuitem"
              title={option.description ?? option.label}
            >
              {renderOption ? renderOption(option, option.id === value) : option.label}
            </button>
          ))
        ) : (
          <div className="px-4 py-3 text-sm text-gray-400 italic text-center">
            {isLoading ? 'Loading...' : 'No results found'}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div ref={dropdownRef} className={`relative flex flex-col ${className}`}>
      <div
        ref={fieldRef}
        className={`relative bg-white border rounded-200 p-4 h-[70px] flex items-center justify-between cursor-pointer
          ${error ? 'border-destructive-dark' : 'border-black-200 hover:border-primary-hover'}
          ${disabled ? 'bg-gray-50 cursor-not-allowed text-black-200' : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-labelledby={`${id}-label`}
        aria-controls={`${id}-listbox`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={disabled}
        aria-describedby={error ? `${id}-error` : helperText ? `${id}-helper` : undefined}
      >
        <div className="flex flex-col gap-1">
          <span
            id={`${id}-label`}
            className={`${error ? 'text-destructive-dark' : 'text-black-600'} font-medium text-detail-90`}
          >
            {label}
            {required && ' *'}
          </span>
          <span
            className={`font-medium text-detail-90 ${hasValue ? 'text-gray-1100' : 'text-black-600'}`}
          >
            {hasValue ? options.find((opt) => opt.id === value)?.label : placeholder}
          </span>
        </div>
        <Icon
          icon={isOpen ? IconName.ChevronUp : IconName.ChevronDown}
          size={16}
          className={error ? 'text-destructive-dark' : 'text-primary'}
        />
      </div>

      {/* Dropdown Menu Portal */}
      {isOpen &&
        createPortal(
          renderMenu(),
          document.getElementById('root-food-shelter-love') || document.body
        )}

      {/* Helper/Error Text below */}
      {(error || helperText) && (
        <p
          className={`mt-2 text-[12px] ${error ? 'font-medium text-destructive-text-error' : 'text-gray-600'}`}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
};

export default DropdownInput;
